import {
    PrintReceiptRequest,
    EmailReceiptRequest,
} from '@b2bsoft/kiosk-common-sdk';
import { observable, makeObservable } from 'mobx';
import { http, logger, LogEvents, Messages, ReceiptGenerator } from 'helpers';

import {
    ReceiptGenerateResponse,
    ReceiptPurpose,
    ReceiptResponse,
    ReceiptStep,
    ReceiptType,
} from 'interfaces';

import { Billpay } from './Billpay';
import StoreModel from '../Index';
import { printReceipt } from 'sdk/InternalSdk';

export class Receipts {
    constructor(private root: StoreModel, private billpay: Billpay) {
        makeObservable(this);
    }

    @observable step?: ReceiptStep;
    @observable generateFailed: boolean = false;

    /**
     * Printing Sale receipt or Offline receipt based on if transaction was created or no in our system
     * @returns
     * @memberof Billpay
     */
    printReceipt = async () => {
        try {
            const response = await this.generateReceipt(ReceiptPurpose.Printer);
            const printResponse = await printReceipt(
                this.root,
                this.getPrintRequest(response.data)
            );

            if (!printResponse.isSuccess) {
                console.error(
                    `${printResponse.errorMessage}. PrinterStatusCode: ${printResponse.printerStatusCode}`
                );

                throw new Error(printResponse.errorMessage);
            }
            return printResponse;
        } catch (error) {
            logger.logError(
                `Failed to print receipt for transactionId: ${this.billpay.transactionId}.`,
                LogEvents.ReceiptPrintFailed,
                { error, billpay: this }
            );

            throw error;
        }
    };

    /**
     * Print custom offline receipt
     * @memberof Billpay
     */
    printOfflineReceipt = async () => {
        const response = await this.generateOfflineReceipt(
            ReceiptPurpose.Printer
        );

        return await printReceipt(
            this.root,
            this.getPrintRequest(response.data)
        );
    };

    /**
     * Email Sale receipt or Offline receipt based on if transaction was created or no in our system
     * @memberof Billpay
     */
    emailReceipt = async (email: string) => {
        const response = await this.generateReceipt(ReceiptPurpose.Email);

        const emailReceiptRequest = {
            transactionId: this.billpay.transactionId,
            amount: this.billpay.amountInserted,
            email,
            receiptData: response.data,
            mediaType: ReceiptType[response.type],
        } as EmailReceiptRequest;

        return await this.root.emailReceipt(emailReceiptRequest);
    };

    /**
     * Generate Sale receipt data or Offline receipt data based on if transaction was created or no in our system
     * @param {ReceiptType} [type=ReceiptType.HTML]
     * @returns {Promise<ReceiptGenerateResponse>}
     * @memberof Billpay
     */
    private generateReceipt = async (
        purpose: ReceiptPurpose
    ): Promise<ReceiptGenerateResponse> => {
        if (
            !this.billpay.transactionId ||
            !this.root.peripheryStatus.kioskOnline
        ) {
            return await this.generateOfflineReceipt(purpose);
        }

        const now = new Date();
        const offsetInHours = now.getHours() - now.getTimezoneOffset() / 60;
        const receiptTime = new Date(now.setHours(offsetInHours)).toISOString();

        const type =
            purpose === ReceiptPurpose.Printer
                ? ReceiptType.Html
                : ReceiptType.Pdf;

        const request = {
            accountNumber: `***${this.billpay.accountNumberLast4}`,
            mediaType: type.toString(),
            warningDisclaimer: !this.billpay.isSuccess
                ? Messages.warningDisclaimer(this.billpay.amountInserted)
                : null,
            receiptTime: receiptTime,
        };

        return http
            .post<ReceiptResponse>(
                `/api/payment/${this.billpay.transactionId}/receipt`,
                request
            )
            .then(async (response) => {
                const receipt = response.data;

                if (receipt == null) {
                    throw new Error("Server didn't returned receipt");
                }

                // PDF receipt always come as one page
                // HTML receipt requires additional manipulations like injecting CSS Styles, since they are comming separately
                return {
                    type,
                    data:
                        type === ReceiptType.Pdf
                            ? receipt.receiptPages[0].content
                            : await new ReceiptResponse(receipt).imageBase64(),
                };
            })
            .catch((error) => {
                logger.logError(
                    `Failed to generate receipt for transactionId: ${this.billpay.transactionId}.`,
                    LogEvents.ReceiptGenerateFailed,
                    { error, billpay: this }
                );

                this.generateFailed = true;

                return this.generateOfflineReceipt(purpose);
            });
    };

    private async generateOfflineReceipt(
        purpose: ReceiptPurpose
    ): Promise<ReceiptGenerateResponse> {
        const data =
            await ReceiptGenerator.generateCustomFailedTransactionReceipt(
                this.billpay.customer!.accountNumber,
                this.billpay.amountInserted,
                Messages.warningDisclaimer(this.billpay.amountInserted),
                this.root
            );

        return {
            // Only image is currently supported
            type: ReceiptType.Image,
            data:
                purpose === ReceiptPurpose.Email
                    ? data.replace('data:image/png;base64,', '')
                    : data,
        };
    }

    private getPrintRequest = (data: string): PrintReceiptRequest => {
        return {
            amount: this.billpay.amountInserted,
            transactionId: this.billpay.transactionId || -1,
            receiptData: data,
        } as PrintReceiptRequest;
    };

    reset = () => {
        this.generateFailed = false;
        this.step = undefined;
    };
}
